import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent } from './ui/tabs';
import { toast } from 'sonner';
import { Settings, Shield, Users, BarChart3 } from 'lucide-react';
import axios from 'axios';

const Login = () => {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [isLoading, setIsLoading] = useState(false);

  const [loginData, setLoginData] = useState({
    email: localStorage.getItem('rememberedEmail') || '',
    password: localStorage.getItem('rememberedPassword') || ''
  });
  
  const [rememberMe, setRememberMe] = useState(
    localStorage.getItem('rememberMe') === 'true'
  );
  const [loginNotice] = useState(() => { const n = sessionStorage.getItem('login_notice'); sessionStorage.removeItem('login_notice'); return n; });

  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'office_staff'
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    
    // Secret kiosk toggle: type "kiosk" as email and press Sign In
    if (loginData.email.trim().toLowerCase() === 'kiosk') {
      if (localStorage.getItem('shop_kiosk_mode') === 'true') {
        localStorage.removeItem('shop_kiosk_mode');
        toast.success('Kiosk mode disabled');
      } else {
        localStorage.setItem('shop_kiosk_mode', 'true');
        toast.success('Kiosk mode enabled');
      }
      setTimeout(() => window.location.reload(), 500);
      return;
    }
    
    setIsLoading(true);
    
    if (!loginData.email || !loginData.password) {
      setLoginData(prev => ({...prev, error: 'Email and password required'}));
      setIsLoading(false);
      return;
    }
    
    const result = await login(loginData.email, loginData.password);
    
    if (result.success) {
      // Save credentials if "Remember Me" is checked
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', loginData.email);
        localStorage.setItem('rememberedPassword', loginData.password);
        localStorage.setItem('rememberMe', 'true');
      } else {
        // Clear saved credentials if unchecked
        localStorage.removeItem('rememberedEmail');
        localStorage.removeItem('rememberedPassword');
        localStorage.removeItem('rememberMe');
      }
      
      toast.success('Welcome to 365Ops!');
      
      // Full reload to ensure auth state is picked up cleanly. Honor a ?redirect=
      // target (e.g. a forklift QR scan that needs login first) — relative paths only.
      const _params = new URLSearchParams(window.location.search);
      const _redirect = _params.get('redirect');
      window.location.href = (_redirect && _redirect.startsWith('/')) ? _redirect : '/dashboard';
    } else {
      toast.error(result.error);
    }
    
    setIsLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    const result = await register(registerData);
    
    if (result.success) {
      toast.success('Account created successfully! Please login.');
      // Reset form
      setRegisterData({
        email: '',
        password: '',
        full_name: '',
        role: 'office_staff'
      });
    } else {
      toast.error(result.error);
    }
    
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-900 to-slate-900 p-12 flex-col justify-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="w-full h-full" style={{
            backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%233B82F6' fill-opacity='0.1'%3E%3Ccircle cx='7' cy='7' r='7'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")"
          }}></div>
        </div>
        
        <div className="relative z-10">
          <div className="mb-8">
            <img 
              src="/365ops-logo.png" 
              alt="365 Ops" 
              className="h-48 w-auto object-contain mb-4" 
              style={{ filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.3))' }}
            />
            <p className="text-blue-200 text-sm">Operations Management System</p>
          </div>
          
          <h2 className="text-2xl font-semibold text-blue-100 mb-4">
            Complete Millwright Management System
          </h2>
          
          <p className="text-lg text-blue-200 mb-12 leading-relaxed">
            Streamline your millwrighting and custom fabrication operations with comprehensive 
            project tracking, quote management, safety documentation, and real-time collaboration tools.
          </p>
          
          <div className="grid grid-cols-1 gap-6">
            <div className="flex items-center">
              <Shield className="h-6 w-6 text-blue-400 mr-3" />
              <span className="text-blue-100">Secure multi-user access control</span>
            </div>
            <div className="flex items-center">
              <BarChart3 className="h-6 w-6 text-blue-400 mr-3" />
              <span className="text-blue-100">Real-time project analytics</span>
            </div>
            <div className="flex items-center">
              <Users className="h-6 w-6 text-blue-400 mr-3" />
              <span className="text-blue-100">Team collaboration tools</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 text-center">
            <img 
              src="/365ops-logo.png" 
              alt="365 Ops" 
              className="h-36 w-auto object-contain mx-auto mb-3" 
              style={{ filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.3))' }}
            />
            <p className="text-blue-400 text-sm">Operations Management System</p>
          </div>

          <Tabs defaultValue="login" className="w-full">
            <TabsContent value="login">
              <Card className="bg-white/60 backdrop-blur-xl border-white/30">
                <CardHeader>
                  <CardTitle className="text-2xl text-center text-slate-900">Welcome Back</CardTitle>
                  <CardDescription className="text-center text-slate-600">
                    Sign in to your 365Ops account
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loginNotice && (
                    <div className="mb-4 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800" data-testid="login-lockout-notice">{loginNotice}</div>
                  )}
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div>
                      <Label htmlFor="email" className="text-slate-800">Email</Label>
                      <Input
                        id="email"
                        type="text"
                        value={loginData.email}
                        onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                        placeholder="your@email.com"
                        autoComplete="email"
                        data-testid="login-email-input"
                        className="mt-1 bg-white/80 backdrop-blur-sm border-white/50 text-slate-900 placeholder:text-slate-500 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <Label htmlFor="password" className="text-slate-800">Password</Label>
                      <Input
                        id="password"
                        type="password"
                        value={loginData.password}
                        onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                        placeholder="Your password"
                        autoComplete="current-password"
                        data-testid="login-password-input"
                        className="mt-1 bg-white/80 backdrop-blur-sm border-white/50 text-slate-900 placeholder:text-slate-500 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div className="flex items-center">
                      <input
                        id="remember-me"
                        type="checkbox"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-white/50 rounded bg-white/70"
                      />
                      <Label htmlFor="remember-me" className="ml-2 text-sm text-slate-700 cursor-pointer">
                        Remember me
                      </Label>
                    </div>
                    <Button 
                      type="submit" 
                      data-testid="login-submit-btn"
                      className="w-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Signing in...' : 'Sign In'}
                    </Button>
                  </form>
                  {/* Biometric Login */}
                  {window.PublicKeyCredential && loginData.email && (
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <button id="bio-login-btn" onClick={async (evt) => {
                        const btn = evt.currentTarget;
                        btn.textContent = 'Checking biometric...';
                        btn.disabled = true;
                        try {
                          console.log('Biometric: requesting options for', loginData.email);
                          const optR = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/biometric/login-options`, { email: loginData.email });
                          const opts = optR.data;
                          console.log('Biometric: got options, rpId=', opts.rpId, 'creds=', opts.allowCredentials?.length);
                          btn.textContent = 'Touch fingerprint sensor...';
                          const toBuffer = (b64) => {
                            const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4));
                            const b = atob(b64.replace(/-/g, '+').replace(/_/g, '/') + pad);
                            return Uint8Array.from(b, c => c.charCodeAt(0)).buffer;
                          };
                          const assertion = await navigator.credentials.get({
                            publicKey: {
                              challenge: toBuffer(opts.challenge),
                              rpId: opts.rpId,
                              allowCredentials: opts.allowCredentials.map(c => ({ ...c, id: toBuffer(c.id) })),
                              userVerification: opts.userVerification,
                              timeout: opts.timeout,
                            },
                          });
                          console.log('Biometric: got assertion', assertion.id);
                          btn.textContent = 'Verifying...';
                          const toB64 = (buf) => btoa(String.fromCharCode(...new Uint8Array(buf))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
                          const r = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/biometric/login-complete`, {
                            id: assertion.id, email: loginData.email,
                            response: { authenticatorData: toB64(assertion.response.authenticatorData), clientDataJSON: toB64(assertion.response.clientDataJSON), signature: toB64(assertion.response.signature) },
                          });
                          localStorage.setItem('token', r.data.access_token);
                          window.location.href = '/dashboard';
                        } catch (e) {
                          console.error('Biometric error:', e);
                          btn.disabled = false;
                          btn.textContent = 'Use Fingerprint / Face ID';
                          const msg = e.response?.data?.detail || e.message || 'Unknown error';
                          if (e.name === 'NotAllowedError') { /* user cancelled, ignore */ }
                          else {
                            setLoginData(prev => ({...prev, error: 'Biometric: ' + msg}));
                          }
                        }
                      }} className="w-full py-2.5 bg-slate-800 hover:bg-slate-700 disabled:bg-slate-500 text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-all">
                        Use Fingerprint / Face ID
                      </button>
                      {loginData.error && loginData.error.startsWith('Biometric') && (
                        <p className="text-red-500 text-xs mt-1 text-center">{loginData.error}</p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
            
            <TabsContent value="register">
              <Card className="bg-white/60 backdrop-blur-xl border-white/30">
                <CardHeader>
                  <CardTitle className="text-2xl text-center text-slate-900">Join 365Ops</CardTitle>
                  <CardDescription className="text-center text-slate-600">
                    Create your operations management account
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleRegister} className="space-y-4">
                    <div>
                      <Label htmlFor="full_name" className="text-slate-800">Full Name</Label>
                      <Input
                        id="full_name"
                        type="text"
                        value={registerData.full_name}
                        onChange={(e) => setRegisterData({...registerData, full_name: e.target.value})}
                        placeholder="John Doe"
                        required
                        className="mt-1 bg-white/80 backdrop-blur-sm border-white/50 text-slate-900 placeholder:text-slate-500 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <Label htmlFor="reg_email" className="text-slate-800">Email</Label>
                      <Input
                        id="reg_email"
                        type="email"
                        value={registerData.email}
                        onChange={(e) => setRegisterData({...registerData, email: e.target.value})}
                        placeholder="your@email.com"
                        required
                        className="mt-1 bg-white/80 backdrop-blur-sm border-white/50 text-slate-900 placeholder:text-slate-500 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <Label htmlFor="role" className="text-slate-800">Role</Label>
                      <select
                        id="role"
                        value={registerData.role}
                        onChange={(e) => setRegisterData({...registerData, role: e.target.value})}
                        className="mt-1 w-full px-3 py-2 bg-white/80 backdrop-blur-sm border border-white/50 rounded-md text-slate-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      >
                        <option value="">Select role</option>
                        <option value="super_admin">Super Administrator</option>
                        <option value="admin">Administrator</option>
                        <option value="project_manager_estimator">Project Manager/Estimator</option>
                        <option value="office_admin">Office Admin</option>
                        <option value="hr">HR</option>
                        <option value="foreman">Foreman</option>
                        <option value="field_employee">Field Employee</option>
                        <option value="dispatch">Dispatch</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="reg_password" className="text-slate-800">Password</Label>
                      <Input
                        id="reg_password"
                        type="password"
                        value={registerData.password}
                        onChange={(e) => setRegisterData({...registerData, password: e.target.value})}
                        placeholder="Create a password"
                        required
                        className="mt-1 bg-white/80 backdrop-blur-sm border-white/50 text-slate-900 placeholder:text-slate-500 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <Button 
                      type="submit" 
                      className="w-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Creating Account...' : 'Create Account'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default Login;